import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";
import Logger from "../../services/Logger";
import LogDetailDialog from "../setting/syslogs/LogDetailDialog";
import SystemLogService from "../setting/syslogs/SystemLogService";

const LOG_SOURCE = "PromotionEmail";
const PAGE_SIZE = 100;
const MAX_PAGES = 15;

function logMatchesPromotion(row, promo) {
    if (!row || !promo) return false;
    const details = row.details && typeof row.details === "object" ? row.details : {};
    const promoId = promo.id != null ? String(promo.id) : "";
    const logId = details.promotionEmailId != null ? String(details.promotionEmailId) : "";
    if (promoId && logId && promoId === logId) return true;
    if (promo.code && details.code && details.code === promo.code) return true;
    if (promo.code && Array.isArray(details.sentCodes) && details.sentCodes.includes(promo.code)) return true;
    return false;
}

function logEmailLabel(row) {
    const details = row?.details;
    if (!details || typeof details !== "object") return "—";
    if (details.email) return String(details.email);
    const recipients = details.recipients;
    if (!Array.isArray(recipients) || recipients.length === 0) return "—";
    return recipients
        .map((item) => (typeof item === "string" ? item : item?.email))
        .filter(Boolean)
        .join(", ");
}

export default function PromotionSendLogDialog({ open, onClose, promotion }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedLog, setSelectedLog] = useState(null);
    const [detailOpen, setDetailOpen] = useState(false);

    useEffect(() => {
        if (!open || !promotion) {
            return undefined;
        }
        let cancelled = false;
        (async () => {
            setLoading(true);
            setRows([]);
            try {
                const matched = [];
                for (let page = 0; page < MAX_PAGES; page += 1) {
                    const result = await SystemLogService.list(
                        page,
                        PAGE_SIZE,
                        "createdAt:desc",
                        null,
                        LOG_SOURCE,
                        false
                    );
                    if (cancelled) return;
                    if (!result.success || !result.data) break;
                    const content = Array.isArray(result.data.content) ? result.data.content : [];
                    for (const row of content) {
                        if (logMatchesPromotion(row, promotion)) {
                            matched.push(row);
                        }
                    }
                    if (content.length < PAGE_SIZE) break;
                    const total = result.data.page?.totalElements ?? result.data.totalElements;
                    if (typeof total === "number" && (page + 1) * PAGE_SIZE >= total) break;
                }
                if (!cancelled) {
                    setRows(matched);
                }
            } catch (err) {
                Logger.error("Promotion send logs:", err);
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, promotion]);

    const title = promotion?.name || promotion?.code || "Promotion";

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
                <DialogTitle sx={{ m: 0, pr: 6 }}>
                    Send log · {title}
                    <IconButton
                        aria-label="close"
                        onClick={onClose}
                        sx={{ position: "absolute", right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Each recipient send is stored in system_log with source {LOG_SOURCE}.
                    </Typography>
                    <DataGrid
                        rows={rows}
                        columns={[
                            {
                                field: "message",
                                headerName: "Message",
                                flex: 1.2,
                                minWidth: 220,
                            },
                            {
                                field: "email",
                                headerName: "Email",
                                flex: 1,
                                minWidth: 180,
                                valueGetter: (_value, row) => logEmailLabel(row),
                            },
                            {
                                field: "title",
                                headerName: "Title",
                                flex: 1,
                                minWidth: 180,
                                valueGetter: (_value, row) => row?.details?.title || "—",
                            },
                            {
                                field: "createdAt",
                                headerName: "Sent",
                                width: 170,
                                valueFormatter: (value) =>
                                    value ? dayjs(value).format("YYYY-MM-DD HH:mm:ss") : "—",
                            },
                        ]}
                        getRowId={(row) => row.id}
                        loading={loading}
                        autoHeight
                        pageSizeOptions={[15, 25, 50]}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 25 } },
                        }}
                        disableRowSelectionOnClick
                        onRowClick={(params) => {
                            setSelectedLog(params.row);
                            setDetailOpen(true);
                        }}
                        sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
                        localeText={{
                            noRowsLabel: loading ? "Loading…" : "No send logs for this promotion.",
                        }}
                    />
                </DialogContent>
            </Dialog>
            <LogDetailDialog
                open={detailOpen}
                onClose={() => {
                    setDetailOpen(false);
                    setSelectedLog(null);
                }}
                log={selectedLog}
            />
        </>
    );
}
